import type {
  GameState, SetupConfig, FullSettlement, BetStatusData,
  Hole18ScenariosData, Press, BetType, NassauStatus,
  RebuildRoundStateInput, RebuildRoundStateResult,
} from './gameTypes';

export interface MatchStatus {
  front: number;
  back: number;
  overall: number;
}

export interface GameEngine {
  readonly gameType: string;

  settleGame(state: GameState): FullSettlement;

  calculateBetStatus(state: GameState): BetStatusData;

  getMatchStatus?(state: GameState): MatchStatus;

  initializeGameState(config: SetupConfig): GameState;

  updateScore(
    state: GameState,
    playerId: string,
    holeNumber: number,
    grossScore: number | null,
  ): GameState;

  updateGameStateSettings(state: GameState, config: SetupConfig): GameState;

  isRoundComplete(state: GameState): boolean;

  finalizeRound(state: GameState): GameState;

  unfinalizeRound(state: GameState): GameState;

  rebuildRoundState(input: RebuildRoundStateInput): RebuildRoundStateResult;
}

export interface PressCapableEngine extends GameEngine {
  getPressStatuses(state: GameState): Record<string, number>;

  checkForAutoPresses(state: GameState): Press[];

  getMaxPressesAllowed(
    state: GameState,
    betType: BetType,
    side: 'top' | 'bottom',
    selectedHole?: number | null,
  ): number;

  computeHole18Scenarios(state: GameState): Hole18ScenariosData | null;

  addManualPress(
    state: GameState,
    betType: BetType,
    side: 'top' | 'bottom',
    count?: number,
    selectedHole?: number,
  ): GameState;

  addPressOnExistingPress(
    state: GameState,
    pressId: string,
    side: 'top' | 'bottom',
  ): GameState;

  canManualPress(
    state: GameState,
    betType: BetType,
    side: 'top' | 'bottom',
  ): boolean;

  canPressExistingPress(
    state: GameState,
    pressId: string,
    side: 'top' | 'bottom',
  ): boolean;

  getPressRightsForSide(
    state: GameState,
    betType: BetType,
    side: 'top' | 'bottom',
  ): number;

  getRemainingPressesForHole(
    state: GameState,
    betType: BetType,
    side: 'top' | 'bottom',
    holeNumber: number,
  ): number;
}

export function isPressCapable(engine: GameEngine): engine is PressCapableEngine {
  return (
    'checkForAutoPresses' in engine &&
    'getMaxPressesAllowed' in engine &&
    'computeHole18Scenarios' in engine &&
    'addManualPress' in engine &&
    'addPressOnExistingPress' in engine &&
    'canManualPress' in engine &&
    'canPressExistingPress' in engine &&
    'getPressStatuses' in engine &&
    'getPressRightsForSide' in engine &&
    'getRemainingPressesForHole' in engine
  );
}
